import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Loader2, PartyPopper } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { getConsent } from "@/lib/tracking";
import { track } from "@/lib/analytics";

const KEY = "epl_giveaway";
const HIDDEN_ON = ["/admin", "/register", "/payment", "/complete-payment", "/giveaway-terms", "/terms", "/privacy"];

const EntryForm = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const input = useRef(null);
  useEffect(() => {
    input.current?.focus();
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/giveaway/enter", { email, marketing_opt_in: optIn, website });
      track("generate_lead", { form: "giveaway", marketing_opt_in: optIn });
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="giveaway-form">
      <input ref={input} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" autoComplete="email"
        data-testid="giveaway-email-input" aria-label="Email address"
        className="w-full rounded-full border border-white/15 bg-white/[0.04] px-5 py-4 text-white placeholder:text-slate-500 outline-none focus:border-[var(--accent)] transition-colors" />
      <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} className="hidden" aria-hidden="true" />
      <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer" data-testid="giveaway-marketing-label">
        <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} data-testid="giveaway-marketing-checkbox" className="mt-1 h-4 w-4 accent-[var(--accent)]" />
        <span>Yes, send me future Elite Performance Lab promotions and news (optional). You can unsubscribe at any time.</span>
      </label>
      {error && <p data-testid="giveaway-error" className="text-sm text-red-300">{error}</p>}
      <button type="submit" disabled={busy} data-testid="giveaway-submit-button" className="btn-primary w-full !py-4 tracking-wide">
        {busy ? <Loader2 size={18} className="animate-spin" /> : "ENTER THE DRAW"}
      </button>
    </form>
  );
};

export const GiveawayPopup = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (localStorage.getItem(KEY)) return;
    let t;
    const start = () => {
      clearTimeout(t);
      t = setTimeout(() => setOpen(true), 2500);
    };
    if (getConsent() !== null) start();
    window.addEventListener("epl-consent-set", start);
    return () => {
      clearTimeout(t);
      window.removeEventListener("epl-consent-set", start);
    };
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  if (!open || HIDDEN_ON.some((p) => pathname.startsWith(p))) return null;
  function close() {
    if (!localStorage.getItem(KEY)) localStorage.setItem(KEY, "dismissed");
    setOpen(false);
  }
  const finish = () => {
    localStorage.setItem(KEY, "entered");
    setDone(true);
  };
  return (
    <div data-testid="giveaway-popup" className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm animate-[fadeIn_.3s_ease-out]"
      onClick={(e) => e.target === e.currentTarget && close()}>
      <div role="dialog" aria-modal="true" aria-labelledby="giveaway-title"
        className="relative w-full max-w-xl overflow-hidden rounded-[2rem] border border-[var(--accent)]/30 bg-[#0b0f17] shadow-2xl">
        <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-[var(--accent)]/20 blur-3xl pointer-events-none" />
        <button onClick={close} data-testid="giveaway-close-button" aria-label="Close" className="absolute top-4 right-4 z-10 rounded-full p-2 text-slate-400 hover:text-white hover:bg-white/10 transition-colors"><X size={20} /></button>
        <div className="relative px-6 py-8 sm:px-10 sm:py-10">
          {done ? (
            <div data-testid="giveaway-success" className="text-center py-6">
              <PartyPopper size={40} className="mx-auto accent-text" />
              <p className="mt-5 font-display font-extrabold text-3xl tracking-tight">You're in! Good luck in the draw!</p>
              <p className="mt-3 text-sm text-slate-400">One entry per email address.</p>
              <button onClick={close} data-testid="giveaway-done-button" className="btn-ghost mt-8">Continue to site</button>
            </div>
          ) : (
            <>
              <p className="eyebrow">Giveaway</p>
              <h2 id="giveaway-title" data-testid="giveaway-heading" className="mt-4 font-display font-extrabold tracking-tight text-[1.3rem] min-[400px]:text-2xl sm:text-[1.85rem] leading-[1.15] pr-6">🎉 WIN A FREE REGISTRATION!</h2>
              <p className="mt-4 text-slate-300 leading-relaxed">Enter your email address for your chance to win a FREE registration at Elite Performance Lab! Don't miss out on your chance to join us.</p>
              <EntryForm onDone={finish} />
              <p className="mt-5 text-xs text-slate-500 leading-relaxed" data-testid="giveaway-legal-links">
                One entry per email. See the <Link to="/giveaway-terms" onClick={() => setOpen(false)} className="underline hover:text-white" data-testid="giveaway-terms-link">giveaway terms, eligibility & closing date</Link> and our{" "}
                <Link to="/privacy" onClick={() => setOpen(false)} className="underline hover:text-white" data-testid="giveaway-privacy-link">Privacy Policy</Link>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
